import {
  BlockStack,
  Checkbox,
  Divider,
  Icon,
  Scrollable,
  Text,
  TextField,
} from "@shopify/polaris"
import { ImageIcon, SearchIcon } from "@shopify/polaris-icons"

import ProSpinner from "../ProSpinner/ProSpinner"
import { QueryFilterProps } from "./types"

type ResourcePickerInnerProps = {
  type: "product" | "collection"
  handleScrolledToBottom: () => void
  queryValue: string
  handleQueryChange: (value: string) => void
  isLoading: boolean
  items: any[]
  selectedItemsMap: {
    [key: string]: boolean | "indeterminate"
  }
  selectedVariantMap: {
    [key: string]: boolean
  }
  multiple: boolean | number
  handleSelectedVariant: (info: {
    id: string
    pid: string
    checked: boolean
  }) => void
  handleSelectedItem: (item: {
    id: string
    checked: boolean
    variants: string[]
  }) => void
  filter: QueryFilterProps["filter"]
}
const ResourcePickerInner = (props: ResourcePickerInnerProps) => {
  const {
    type,
    isLoading,
    items,
    handleScrolledToBottom,
    queryValue,
    handleQueryChange,
    selectedItemsMap,
    selectedVariantMap,
    handleSelectedVariant,
    handleSelectedItem,
    multiple,
    filter,
  } = props

  const showVariant = filter?.variants ?? true

  const renderItem = (item: any) => {
    const { id, title, image, productsCount, variants, featuredImage } =
      item as any

    function isDisabled() {
      const selectedCount = Object.keys(selectedItemsMap).length
      if (multiple === false) {
        // 单选模式：如果当前项未选中并且已选中项数量大于 0，则禁用
        return !selectedItemsMap[id] && selectedCount >= 1
      }
      if (typeof multiple === "number") {
        // 限制最大选中数量：如果当前项未选中且已选中项达到最大限制，则禁用
        return !selectedItemsMap[id] && selectedCount >= multiple
      }
      return false
    }

    const renderImage = (info: any) => {
      if (info?.url) {
        return (
          <img
            src={info.url}
            alt={info.altText}
            className="mr-4 h-10 w-10 rounded-lg border border-gray-200"
          />
        )
      }
      return (
        <div className="mr-4 flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200">
          <Icon source={ImageIcon} tone="subdued" />
        </div>
      )
    }
    return (
      <div key={id}>
        <div className="sticky top-10 z-[9] min-h-14 bg-white">
          <BlockStack gap="100">
            <div className="flex items-center py-1">
              <Checkbox
                label=""
                disabled={isDisabled()}
                checked={selectedItemsMap[id]}
                onChange={(isChecked) => {
                  handleSelectedItem({
                    id: item.id,
                    checked: isChecked,
                    variants:
                      variants?.nodes?.map((variant) => variant.id) ?? [],
                  })
                }}
              />
              <div className="flex items-center flex-1">
                {renderImage(type === "product" ? featuredImage : image)}
                <div className="flex flex-col">
                  <Text variant="bodyMd" fontWeight="medium" as="span">
                    {title}
                  </Text>
                  {type === "collection" && (
                    <Text variant="bodyMd" fontWeight="medium" as="span">
                      {productsCount?.count === 1
                        ? `1 product`
                        : `${productsCount?.count ?? 0} products`}
                    </Text>
                  )}
                </div>
              </div>
            </div>
            <Divider />
          </BlockStack>
        </div>
        {showVariant &&
          variants?.nodes?.map((variant) => {
            return (
              <div key={variant.id} className="min-h-10 pl-8">
                <div className="flex items-center py-1">
                  <Checkbox
                    label={""}
                    disabled={isDisabled()}
                    checked={selectedVariantMap[variant.id]}
                    onChange={(isChecked) => {
                      handleSelectedVariant({
                        id: variant.id,
                        pid: item.id,
                        checked: isChecked,
                      })
                    }}
                  />
                  <div className="grid grid-cols-3 sm:grid-cols-4 flex-1">
                    <div className="col-span-2 max-w-[240px] break-words font-bold">
                      {variant.title}
                    </div>
                    <div className="hidden sm:block col-span-1 text-right">
                      {variant.quantityAvailable} available
                    </div>
                    <span className="col-span-1 text-right">
                      ${variant.price.amount}
                    </span>
                  </div>
                </div>
                <Divider />
              </div>
            )
          })}
      </div>
    )
  }
  const emptyState = (
    <div className="flex h-[320px] w-full items-center justify-center">
      <Text variant="bodyMd" fontWeight="bold" as="span">
        {`Your store doesn't have any ${type}s yet.`}
      </Text>
    </div>
  )

  return (
    <Scrollable
      shadow
      className="px-4 h-[400px] bg-white"
      focusable
      onScrolledToBottom={handleScrolledToBottom}
    >
      {/* 搜索框 */}
      <div className="sticky top-0 z-10 h-10 w-full rounded-sm bg-white shadow-sm mt-4">
        <BlockStack gap="100">
          <TextField
            value={queryValue}
            label=""
            onChange={handleQueryChange}
            prefix={<Icon source={SearchIcon} tone="base" />}
            clearButton
            placeholder={`Search ${type}s`}
            autoComplete="off"
          />
        </BlockStack>
      </div>
      <ProSpinner loading={isLoading} className="min-h-[400px]">
        {items.length > 0 ? items.map((item) => renderItem(item)) : emptyState}
      </ProSpinner>
    </Scrollable>
  )
}
export default ResourcePickerInner
